/**
 * AI Recruit - API Service
 * Centralized API client with authentication and error handling
 */

import axios from 'axios'
import toast from 'react-hot-toast'
import type { SkillWithExperience } from '../types/interview'

// ============================================
// API BASE URL CONFIGURATION
// ============================================
// The backend this build talks to when VITE_API_BASE_URL is not set.
//
// The pipelines DO pass that variable as a Docker build argument
// (azure-pipelines-frontend-{qa,prod}.yml -> `apiBaseUrl` ->
// --build-arg VITE_API_BASE_URL), and when it arrives Vite inlines it and it
// wins over this constant. But it was observed NOT arriving in a real
// deployment: the built bundle contained localhost and every request from
// the deployed site failed, login included.
//
// So this default must stay a real, working backend rather than localhost.
// A wrong-but-reachable environment is recoverable; localhost in a deployed
// bundle is total failure, and it fails at the login screen where it is
// least diagnosable.
//
// For local development set VITE_API_BASE_URL=http://localhost:8000 in
// frontend/.env.local (gitignored, never copied into the Docker image).
//
// dev/UAT : https://ai-resume-backend-dev.yellowhill-86bc6c99.eastus2.azurecontainerapps.io
// prod    : https://ai-resume-backend-prod.yellowhill-86bc6c99.eastus2.azurecontainerapps.io
const DEFAULT_API_BASE_URL = 'https://ai-resume-backend-prod.yellowhill-86bc6c99.eastus2.azurecontainerapps.io'
// const DEFAULT_API_BASE_URL = 'http://localhost:8000'

// Get base URL from environment or use default
export const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || DEFAULT_API_BASE_URL

// Versioned API URLs
export const API_V1_URL = `${API_BASE_URL}/api/v1`
export const API_V2_URL = `${API_BASE_URL}/api/v2`

// Create axios instance for v2 API
const getV2BaseUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_API_URL
  if (envUrl) {
    // Replace /api/v1 with /api/v2
    return envUrl.replace('/api/v1', '/api/v2')
  }
  return API_V2_URL
}

const apiV2 = axios.create({
  baseURL: getV2BaseUrl(),
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ─── Token refresh mutex ────────────────────────────────────────────────────
// Ensures only ONE refresh attempt fires when multiple concurrent requests all
// receive 401 at the same time (common on pages with many parallel queries).
interface RefreshSubscriber {
  resolve: (token: string) => void
  reject: (err: Error) => void
}

let isRefreshing = false
let refreshSubscribers: RefreshSubscriber[] = []

function onRefreshSuccess(newToken: string) {
  refreshSubscribers.forEach(({ resolve }) => resolve(newToken))
  refreshSubscribers = []
}

function doLogout() {
  isRefreshing = false
  const err = new Error('Session expired')
  refreshSubscribers.forEach(({ reject }) => reject(err))
  refreshSubscribers = []
  localStorage.removeItem('ai_recruit_token')
  localStorage.removeItem('ai_recruit_refresh_token')
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

async function handleUnauthorized(
  error: any,
  retryCall: (cfg: any) => Promise<any>
): Promise<any> {
  const originalConfig = error.config

  // Prevent retried requests from re-entering the refresh flow
  if (originalConfig?._retry) {
    doLogout()
    return Promise.reject(error)
  }

  // If another refresh is already in progress, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshSubscribers.push({
        resolve: (newToken) => {
          originalConfig.headers = originalConfig.headers || {}
          originalConfig.headers.Authorization = `Bearer ${newToken}`
          originalConfig._retry = true
          resolve(retryCall(originalConfig))
        },
        reject,
      })
    })
  }

  originalConfig._retry = true
  isRefreshing = true

  const refreshToken = localStorage.getItem('ai_recruit_refresh_token')
  if (!refreshToken) {
    doLogout()
    return Promise.reject(error)
  }

  try {
    // Raw axios call so we don't re-enter our own interceptors
    const refreshResponse = await axios.post(
      `${getV2BaseUrl()}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )

    const newAccessToken = refreshResponse.data.access_token
    const newRefreshToken = refreshResponse.data.refresh_token

    localStorage.setItem('ai_recruit_token', newAccessToken)
    localStorage.setItem('ai_recruit_refresh_token', newRefreshToken)

    isRefreshing = false
    onRefreshSuccess(newAccessToken)

    originalConfig.headers = originalConfig.headers || {}
    originalConfig.headers.Authorization = `Bearer ${newAccessToken}`
    return retryCall(originalConfig)
  } catch {
    doLogout()
    return Promise.reject(error)
  }
}
// ────────────────────────────────────────────────────────────────────────────

// Add request interceptor to apiV2 (attach auth token)
apiV2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ai_recruit_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// apiV2 response interceptor — 401 handled via shared mutex
apiV2.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      return handleUnauthorized(error, (cfg) => apiV2(cfg))
    }
    // 403 = toast + stay on the current page, do not redirect (RBAC_UI_IMPLEMENTATION_PLAN.md
    // §5.5). Unlike a 401, the session is valid — the caller just isn't allowed to do this one
    // thing. This also doubles as a live canary: a 403 firing from an action the UI showed as
    // enabled is a hide/disable bug to fix, not "working as intended."
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : "You don't have permission to do that.")
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: (credentials: { email: string; password: string; remember_me?: boolean }) =>
    apiV2.post('/auth/login', credentials),

  // Live capability set for the current user — backs usePermission().
  // AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §10.1.
  getMyCapabilities: () =>
    apiV2.get('/auth/me/capabilities'),

  register: (userData: {
    email: string
    password: string
    confirm_password: string
    first_name: string
    last_name: string
    username?: string
    phone?: string
    organization_name?: string
    organization_domain?: string
    organization_industry?: string
  }) => apiV2.post('/auth/register', userData),

  logout: (revoke_all_sessions?: boolean) =>
    apiV2.post('/auth/logout', {}, {
      params: revoke_all_sessions ? { revoke_all_sessions } : {}
    }),

  getCurrentUser: () => apiV2.get('/auth/me'),

  refreshToken: (refresh_token: string) =>
    apiV2.post('/auth/refresh', { refresh_token }),

  changePassword: (passwordData: {
    current_password: string
    new_password: string
    confirm_password: string
  }) => apiV2.post('/auth/change-password', passwordData),

  requestPasswordReset: (email: string) =>
    apiV2.post('/auth/request-password-reset', { email, frontend_url: window.location.origin }),

  resetPassword: (resetData: {
    token: string
    new_password: string
    confirm_password: string
  }) => apiV2.post('/auth/reset-password', resetData),

  verifyEmail: (token: string) =>
    apiV2.post('/auth/verify-email', { token }),

  resendVerification: () =>
    apiV2.post('/auth/resend-verification'),

  validateToken: () =>
    apiV2.get('/auth/validate-token'),
}

// Resumes API
export const resumesApi = {
  upload: (formData: FormData) =>
    apiV2.post('/resumes/upload', formData, {  // Changed to apiV2 for Foundry integration
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Batch status check for plain (job-less) resume uploads — used by the bulk
  // upload dialog on the Candidates page to poll many resumes in one request.
  getBulkUploadStatus: (resumeIds: string[]) =>
    apiV2.post('/resumes/bulk-upload-status', { resume_ids: resumeIds }),


  uploadAndMatch: (jobId: string, formData: FormData) =>
    apiV2.post(`/resumes/upload-and-match/${jobId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 120 seconds (2 minutes) - includes duplicate detection
    }),
  
  // Upload constraints for the bulk apply dialog (max files / size / types).
  // Server-driven so the limits can change without a frontend rebuild.
  getBulkUploadConfig: () => apiV2.get('/resumes/bulk-upload-config'),

  // A recruiter's own upload activity for the last `days` days — backs the
  // notification/history panel on the Candidates page, so they don't need
  // to keep the bulk-upload popup open to find out what happened to a file.
  getMyUploadHistory: (days: number = 3) =>
    apiV2.get('/resumes/my-upload-history', { params: { days } }),

  // Batch version of getUploadAndMatchStatus — polls many resumes for one job
  // in a single request (used by the bulk apply dialog on the job detail page).
  getBulkUploadAndMatchStatus: (jobId: string, resumeIds: string[]) =>
    apiV2.post(`/resumes/bulk-upload-and-match-status/${jobId}`, { resume_ids: resumeIds }),

  // Get upload-and-match status (poll this endpoint)
  getUploadAndMatchStatus: (resumeId: string, jobId: string) =>
    apiV2.get(`/resumes/${resumeId}/upload-and-match-status/${jobId}`),
  
  list: (params?: {
    page?: number
    page_size?: number
    status?: string
    search?: string
    sort_by?: string
    sort_order?: string
    source_id?: string
  }) => apiV2.get('/resumes/list', { params }),
  
  getById: (id: string) => apiV2.get(`/resumes/${id}/analysis`),
  
  reprocess: (id: string) => apiV2.post(`/resumes/${id}/reprocess`, {}, {
    timeout: 180000 // 3 minutes timeout for resume reprocessing
  }),
  
  delete: (id: string) => apiV2.delete(`/resumes/${id}`),

  download: (id: string, jobId?: string) => apiV2.get(`/resumes/${id}/download${jobId ? `?job_id=${jobId}` : ''}`, {
    responseType: 'blob'
  }),

  semanticSearch: (searchData: {
    query: string
    max_results?: number
    include_analysis?: boolean
  }) => apiV2.post('/resumes/search/semantic', searchData, {
    timeout: 120000 // 2 minutes timeout for semantic search
  }),
  
  // Duplicate management endpoints
  listDuplicates: (params?: {
    page?: number
    page_size?: number
    confidence_threshold?: number
  }) => apiV2.get('/resumes/duplicates', { params }),

  reviewDuplicate: (id: string, reviewData: {
    action: 'merge' | 'keep_both' | 'delete_duplicate'
    primary_resume_id?: string
    notes?: string
  }) => apiV2.post(`/resumes/${id}/review-duplicate`, reviewData),

  getDuplicateDetails: (id: string) => apiV2.get(`/resumes/${id}/duplicate-details`),

  // AI comparison endpoint
  compare: (data: {
    resume_ids: string[]
    comparison_criteria?: Record<string, number>
  }) => apiV2.post('/resumes/compare', data),

  // Duplicate detection endpoint (V2 - uses Foundry ContactExtractionAgent for consistency)
  searchDuplicates: (formData: FormData) =>
    apiV2.post('/resumes/search-duplicates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000 // 90 seconds timeout for duplicate check (Qdrant + Azure latency)
    }),

  // Blacklist duplicates endpoint
  blacklistDuplicates: (blacklistData: { blacklisted_resume_ids: string[] }) =>
    apiV2.post(`/resumes/blacklist-duplicates`, { resume_ids: blacklistData.blacklisted_resume_ids }),

  // Unblacklist duplicates endpoint
  unblacklistDuplicates: (unblacklistData: { unblacklisted_resume_ids: string[] }) =>
    apiV2.post(`/resumes/unblacklist-duplicates`, { resume_ids: unblacklistData.unblacklisted_resume_ids }),
}

// Job Assignment Interfaces
export interface JobAssignment {
  id: string;
  name: string;
  email: string;
  role: string;
  assigned_at: string;
}

export interface UpdateAssignmentsRequest {
  user_ids: string[];
}

// Jobs API - Using V2 endpoints with Foundry agents
export const jobsApi = {
  create: (jobData: any) => apiV2.post('/jobs/', jobData, { timeout: 120000 }),

  analyzeText: (analysisData: {
    job_description_text: string
    company_info?: any
  }) => apiV2.post('/jobs/analyze-text', analysisData, { timeout: 180000 }),

  uploadJD: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiV2.post('/jobs/upload-jd', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    })
  },

  list: (params?: {
    page?: number
    page_size?: number
    status?: string
    client?: string
    search?: string
    source_id?: string
    sort_by?: string
    sort_order?: string
  }) => apiV2.get('/jobs/', { params }),

  listClients: () => apiV2.get('/jobs/clients'),

  search: (params: {
    query: string
    page?: number
    page_size?: number
  }) => apiV2.get('/jobs/', {
    params: { search: params.query, page: params.page, page_size: params.page_size },
  }),

  getById: (id: string) => apiV2.get(`/jobs/${id}`),

  update: (id: string, jobData: any) => apiV2.put(`/jobs/${id}`, jobData),

  updateStatus: (id: string, status: string) => apiV2.patch(`/jobs/${id}/status`, { status }),

  reprocess: (id: string) => apiV2.post(`/jobs/${id}/reprocess`, {}, { timeout: 180000 }),

  /** The original uploaded JD file (Upload JD path only) -- mirrors
   *  resumesApi.download exactly. 404s for a job with no original file. */
  download: (id: string) => apiV2.get(`/jobs/${id}/download`, {
    responseType: 'blob'
  }),

  delete: (id: string) => apiV2.delete(`/jobs/${id}`),

  // Job Assignment Management
  getJobRecruiters: (jobId: string) => apiV2.get(`/jobs/${jobId}/recruiters`),

  updateJobRecruiters: (jobId: string, userIds: string[]) =>
    apiV2.put(`/jobs/${jobId}/recruiters`, { user_ids: userIds }),

  addJobRecruiter: (jobId: string, userId: string) =>
    apiV2.post(`/jobs/${jobId}/recruiters/${userId}`, {}),

  removeJobRecruiter: (jobId: string, userId: string) =>
    apiV2.delete(`/jobs/${jobId}/recruiters/${userId}`),
}

// One round's acknowledgement state, as returned by
// GET /matching/jobs/{jobId}/candidates/{resumeId}/stage-acknowledgements.
export interface StageAcknowledgement {
  round_code: string
  label: string
  decision: 'Selected' | 'Rejected'
  status: 'not_sent' | 'sent' | 'acknowledged'
  sent_at: string | null
  last_sent_at: string | null
  resend_count: number
  acknowledged_at: string | null
  feedback_text: string | null
}

// Matching API - Using V2 endpoints with Foundry JobMatchingAgent
export const matchingApi = {
  matchCandidatesToJob: (jobId: string, params?: {
    resume_ids?: string[]
    custom_weights?: Record<string, number>
    min_score_threshold?: number
    max_results?: number
  }) => apiV2.post(`/matching/jobs/${jobId}/candidates`, params || {}),

  getMatches: (jobId: string, params?: {
    page?: number
    page_size?: number
    min_score?: number
  }) => apiV2.get(`/matching/jobs/${jobId}/matches`, { params }),

  updateWeights: (jobId: string, weights: Record<string, number>) =>
    apiV2.put(`/matching/jobs/${jobId}/weights`, { weights }),

  // Candidate-to-Job matching (find jobs for a specific candidate)
  matchJobsToCandidate: (resumeId: string, params?: {
    max_results?: number
    min_score?: number
  }) => apiV2.post(`/matching/candidates/${resumeId}/jobs`, params || {}),

  // Get stored job matches for a candidate (from database - much faster)
  getJobMatchesForCandidate: (resumeId: string) =>
    apiV2.get(`/matching/candidates/${resumeId}/jobs`),

  // Search resumes by name, email, phone, skills or title
  searchResumes: (params: {
    query: string
    page?: number
    page_size?: number
  }) => apiV2.get('/resumes/list', { params: { search: params.query, page: params.page, page_size: params.page_size } }),

  // Compare multiple candidates against a job (async)
  compareCandidatesAsync: (jobIds: string[], resumeIds: string[]) =>
    apiV2.post('/matching/compare-candidates-async', { job_ids: jobIds, resume_ids: resumeIds }),

  // Get task status
  getTaskStatus: (taskId: string) =>
    apiV2.get(`/matching/compare-status/${taskId}`),

  // Compare multiple candidates against a job (sync)
  compareCandidates: (jobId: string, resumeIds: string[]) =>
    apiV2.post('/matching/compare-candidates', { job_id: jobId, resume_ids: resumeIds }, { timeout: 300000 }),

  // Get stored comparison results for a job
  getComparisonResults: (jobId: string) =>
    apiV2.get(`/matching/jobs/${jobId}/comparison-results`),

  // Delete a specific comparison record
  deleteComparisonRecord: (jobId: string, resumeId: string) =>
    apiV2.delete(`/matching/jobs/${jobId}/comparison-results/${resumeId}`),

  // Reprocess a specific comparison record
  reprocessComparisonRecord: (jobId: string, resumeId: string) =>
    apiV2.post(`/matching/jobs/${jobId}/comparison-results/${resumeId}/reprocess`, {}),

  // Candidate Status Management (configurable Candidate Stage/Status model)
  // `candidateStatusId` is the new source of truth; a comment/reason is required.
  updateApplicationStatus: (jobId: string, resumeId: string, candidateStatusId: string, comment: string) =>
    apiV2.patch(`/matching/jobs/${jobId}/candidates/${resumeId}/application-status`, {
      candidate_status_id: candidateStatusId,
      comment,
    }),

  getApplicationStatus: (jobId: string, resumeId: string) =>
    apiV2.get(`/matching/jobs/${jobId}/candidates/${resumeId}/application-status`),

  // Stage Acknowledgements — one row per pipeline round (Screening/L1/L2/L3/
  // HR/Offer/BGV) currently resolved (Selected/Rejected) for this candidate.
  // Empty array = nothing resolved yet, panel renders nothing.
  getStageAcknowledgements: (jobId: string, resumeId: string) =>
    apiV2.get<StageAcknowledgement[]>(`/matching/jobs/${jobId}/candidates/${resumeId}/stage-acknowledgements`),

  // Send/resend the acknowledgement mail (with decision PDF) for one round.
  sendStageAcknowledgement: (jobId: string, resumeId: string, roundCode: string) =>
    apiV2.post(`/matching/jobs/${jobId}/candidates/${resumeId}/stage-acknowledgement/${roundCode}/send`, {}),

  // Get all comparison results across all jobs (global view with optional filters)
  getAllMatchingResults: (params?: {
    job_id?: string
    resume_id?: string
    candidate_status_id?: string
    status_code?: string
    matched_by_id?: string
    min_score?: number
    max_score?: number
    page?: number
    page_size?: number
  }) => apiV2.get('/matching/all-results', { params }),
}

// Candidate Stage / Status master data API
export const candidateStatusApi = {
  getStages: () => apiV2.get('/candidate-stages'),
  getStatuses: () => apiV2.get('/candidate-statuses'),
}

// Analytics API
export const analyticsApi = {
  getDashboardStats: (queryString?: string) =>
    apiV2.get(`/analytics/dashboard${queryString ? `?${queryString}` : ''}`),

  getResumeStats: (params?: {
    start_date?: string
    end_date?: string
  }) => apiV2.get('/analytics/resumes', { params }),

  getJobStats: (params?: {
    start_date?: string
    end_date?: string
  }) => apiV2.get('/analytics/jobs', { params }),

  getMatchingStats: () => apiV2.get('/analytics/matching'),

  getProcessingStats: () => apiV2.get('/analytics/processing'),

  getTeamDashboardStats: (queryString?: string) =>
    apiV2.get(`/analytics/team-dashboard${queryString ? `?${queryString}` : ''}`),

  getAgentCostDashboard: (params: { from: string; to: string; workspace_id?: string }) =>
    apiV2.get('/analytics/usage/cost-dashboard', { params }),

  getAgentRuns: (agentName: string, params: { from: string; to: string; workspace_id?: string }) =>
    apiV2.get(`/analytics/usage/agents/${encodeURIComponent(agentName)}/runs`, { params }),

  getWorkflowRuns: (workflowName: string, params: { from: string; to: string; workspace_id?: string }) =>
    apiV2.get(`/analytics/usage/workflows/${encodeURIComponent(workflowName)}/runs`, { params }),
}


// Users API
export const usersApi = {
  list: (params?: { search?: string; role?: string; role_id?: string; is_active?: boolean; page?: number; page_size?: number }) => {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.role_id) queryParams.append('role_id', params.role_id)
    else if (params?.role) queryParams.append('role', params.role)
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active))
    if (params?.page) queryParams.append('page', String(params.page))
    if (params?.page_size) queryParams.append('page_size', String(params.page_size))
    const queryString = queryParams.toString()
    return apiV2.get(`/users/${queryString ? `?${queryString}` : ''}`)
  },

  // Get all recruiters (no pagination) for assignment dropdowns
  getAllRecruiters: () => apiV2.get('/users/recruiters/all'),

  // Every role (built-in + custom) that can be offered in a role picker —
  // gated on users.view (split 2026-08-23 from the old shared
  // users.manage), not roles.manage. The Create/Edit User forms' role
  // dropdown depends on this call succeeding.
  getAvailableRoles: () => apiV2.get('/users/roles/available'),

  create: (userData: any) => apiV2.post('/users/', userData),

  update: (id: string, userData: any) => apiV2.put(`/users/${id}`, userData),

  delete: (id: string) => apiV2.delete(`/users/${id}`),

  updateProfile: (profileData: any) => apiV2.put('/users/me', profileData),

  resetPassword: (id: string) => apiV2.post(`/users/${id}/reset-password`, {}),
  setPassword: (id: string, newPassword: string) => apiV2.post(`/users/${id}/set-password`, { new_password: newPassword }),
}

// Virtual Interviews API
export const interviewsApi = {
  // Create a new interview session
  createSession: (data: {
    job_id: string;
    resume_id: string;
    interview_type?: string;
    max_questions?: number;
    target_duration_minutes?: number;
    confidence_threshold?: number;
    interview_config?: any;
  }) => apiV2.post('/interviews/sessions', data),

  // List interview sessions
  list: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    interview_type?: string;
  }) => apiV2.get('/interviews/sessions', { params }),

  // Get interview session details
  getSessionDetails: (sessionId: string) => apiV2.get(`/interviews/sessions/${sessionId}`),

  // Get interview session status
  getSessionStatus: (sessionId: string) => apiV2.get(`/interviews/sessions/${sessionId}/status`),

  // Submit a response to current question
  submitResponse: (sessionId: string, data: {
    response_text: string;
    response_time_seconds: number;
  }) => apiV2.post(`/interviews/sessions/${sessionId}/responses`, data),

  // Complete interview and get assessment
  completeInterview: (sessionId: string) => apiV2.post(`/interviews/sessions/${sessionId}/complete`),

  // Cancel interview session
  cancelInterview: (sessionId: string) => apiV2.delete(`/interviews/sessions/${sessionId}`),
}

// AI Interviews API (v2 - with audio recording and Azure agents)
export const aiInterviewsApi = {
  // Create a new AI interview
  create: (data: {
    job_id: string;
    resume_id: string;
    interview_type?: 'ai_interview' | 'express' | 'standard';
    selected_skills?: SkillWithExperience[];
  }) => apiV2.post('/ai-interviews', data),

  // List AI interviews with filtering
  list: (params?: {
    job_id?: string;
    resume_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }) => apiV2.get('/ai-interviews', { params }),

  // List interviews by resume
  listByResume: (resumeId: string, params?: {
    status?: string;
    page?: number;
    page_size?: number;
  }) => apiV2.get('/ai-interviews', { params: { resume_id: resumeId, ...params } }),

  // List interviews by job
  listByJob: (jobId: string, params?: {
    status?: string;
    page?: number;
    page_size?: number;
  }) => apiV2.get('/ai-interviews', { params: { job_id: jobId, ...params } }),

  // Get interview details
  getDetails: (interviewId: string) => apiV2.get(`/ai-interviews/${interviewId}`),

  // Generate interview questions via Azure agent
  generateQuestions: (interviewId: string) => apiV2.post(`/ai-interviews/${interviewId}/generate-questions`, {}, {
    timeout: 120000 // 2 minutes timeout for question generation
  }),

  // Upload audio/video recording
  uploadAudio: (
    interviewId: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ) => apiV2.post(`/ai-interviews/${interviewId}/upload-audio`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 900000, // 15 minutes for large video uploads
    onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
      if (onProgress && progressEvent.total) {
        onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
      }
    }
  }),

  // NOTE: generateTranscript and generateAnalysis endpoints have been removed.
  // Interview processing is now handled automatically by Celery workflow after audio upload.

  // Update interview status
  updateStatus: (interviewId: string, status: string) => apiV2.patch(`/ai-interviews/${interviewId}/status`, { status }),

  // Get interview threshold configuration
  getConfig: () => apiV2.get('/ai-interviews/config/threshold'),

  // Delete an AI interview (admin only)
  delete: (interviewId: string) => apiV2.delete(`/ai-interviews/${interviewId}`),

  // Get job skills for skill selection (Express/Standard interviews)
  getJobSkills: (jobId: string) => apiV2.get(`/ai-interviews/jobs/${jobId}/skills`),

  // Look up cached questions for a skill + years of experience from question base
  getCachedQuestions: (skill: string, years: number) =>
    apiV2.get('/ai-interviews/skills/cached-questions', { params: { skill, years } }),

  // Generate skill-based questions for Express/Standard interviews
  generateSkillQuestions: (interviewId: string) =>
    apiV2.post(`/ai-interviews/${interviewId}/generate-skill-questions`, {}, {
      timeout: 300000 // 5 minutes for Standard (3 skills)
    }),

  // Generate additional questions for a skill (Express/Standard interviews)
  generateAdditionalQuestions: (
    interviewId: string,
    data: {
      skill: string;
      category?: 'core_conceptual' | 'practical' | 'scenario_based';
      custom_prompt?: string;
    }
  ) => apiV2.post(`/ai-interviews/${interviewId}/additional-questions`, data, {
    timeout: 120000 // 2 minutes timeout
  }),

  // Update selected questions (remove unselected questions)
  updateSelectedQuestions: (
    interviewId: string,
    selectedIndices: number[]
  ) => apiV2.put(`/ai-interviews/${interviewId}/questions`, {
    selected_question_indices: selectedIndices
  }),

  // Add new questions to an interview
  addQuestionsToInterview: (
    interviewId: string,
    questions: Array<{
      question: string;
      skill: string;
      skill_aspect_tested?: string;
      category?: string;
      expected_answer?: { key_points: string[] };
      is_additional?: boolean;
    }>
  ) => apiV2.post(`/ai-interviews/${interviewId}/questions/add`, { questions }),

  // Trigger immediate reprocessing of a stuck interview
  processNow: (interviewId: string) => apiV2.post(`/ai-interviews/${interviewId}/process-now`),
}

export const resumeGenerationApi = {
  // Generate clean resume with extended timeout (5 minutes)
  generateCleanResume: (resumeId: string) =>
    apiV2.get(`/resumes/${resumeId}/generate-clean-resume`, {
      responseType: 'blob',
      timeout: 300000, // 5 minutes timeout for resume generation
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      }
    }),
}

// Teams API
export const teamsApi = {
  list: () =>
    apiV2.get('/teams'),

  get: (teamId: string) =>
    apiV2.get(`/teams/${teamId}`),

  create: (data: { name: string; manager_id: string }) =>
    apiV2.post('/teams', data),

  update: (teamId: string, data: { name?: string; manager_id?: string }) =>
    apiV2.put(`/teams/${teamId}`, data),

  delete: (teamId: string) =>
    apiV2.delete(`/teams/${teamId}`),

  addMember: (teamId: string, userId: string) =>
    apiV2.post(`/teams/${teamId}/members`, { user_id: userId }),

  removeMember: (teamId: string, userId: string) =>
    apiV2.delete(`/teams/${teamId}/members/${userId}`),
}

// Roles & Permissions API (dynamic RBAC admin — AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §9)
export const rolesApi = {
  list: () =>
    apiV2.get('/admin/roles'),

  get: (roleId: string) =>
    apiV2.get(`/admin/roles/${roleId}`),

  create: (data: { name: string; description?: string; permission_keys?: string[] }) =>
    apiV2.post('/admin/roles', data),

  update: (roleId: string, data: { name?: string; description?: string }) =>
    apiV2.put(`/admin/roles/${roleId}`, data),

  // `reason` is optional audit-trail text — only meaningful for a
  // destructive delete, so the frontend only prompts for it there; the
  // backend accepts it whenever it's given, `undefined` when skipped.
  delete: (roleId: string, reason?: string) =>
    apiV2.delete(`/admin/roles/${roleId}`, reason ? { data: { reason } } : undefined),

  updatePermissions: (roleId: string, permissionKeys: string[], reason?: string) =>
    apiV2.put(`/admin/roles/${roleId}/permissions`, { permission_keys: permissionKeys, reason }),

  auditLog: (roleId: string) =>
    apiV2.get(`/admin/roles/${roleId}/audit-log`),
}

export const permissionsApi = {
  list: () =>
    apiV2.get('/admin/permissions'),
}

export const auditLogApi = {
  list: (params?: {
    entity_type?: string
    action?: string
    actor_user_id?: string
    actor_email?: string
    start_date?: string
    end_date?: string
    limit?: number
    offset?: number
  }) => apiV2.get('/admin/audit-log', { params }),
}

export { apiV2 }
export default apiV2